import { Link } from "react-router-dom";

import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import ArticleLayout from "./ArticleLayout";
import RelatedGuides from "./RelatedGuides";

export default function FoiParcursPfaPage() {
  return (
    <>
      <Seo {...seoFromPublicPage("/foi-parcurs-pfa")} />
      <ArticleLayout
        eyebrow="Pentru PFA"
        title="Foi de parcurs pentru PFA: obligații, deducere și model"
        lead="PFA-urile care folosesc un autovehicul în activitatea independentă au exact aceleași obligații ca firmele dacă vor să deducă integral cheltuielile. Iată ce trebuie să faci, indiferent dacă mașina e personală, în comodat sau cumpărată pe PFA."
      >
        <h2>PFA și autovehicul: cele trei situații tipice</h2>
        <p>
          Pentru un PFA, autovehiculul folosit în activitate poate fi într-una din trei
          situații. În toate cazurile, regulile fiscale sunt aceleași ca pentru SRL-uri:
          <strong> deducere 50% fără foaie</strong> sau{" "}
          <strong>100% cu foaie de parcurs</strong> și utilizare exclusiv economică.
        </p>

        <h3>Mașina personală a titularului</h3>
        <p>
          Utilizată prin <em>contract de comodat</em> încheiat între persoana fizică
          (proprietar) și PFA (utilizator). Fără comodat, cheltuielile nu pot fi deduse.
          Detalii în{" "}
          <Link to="/foi-parcurs-comodat">ghidul despre foi de parcurs și comodat</Link>.
        </p>

        <h3>Mașină achiziționată pe PFA</h3>
        <p>
          Înregistrată ca mijloc fix în evidența PFA-ului, cu factură pe CUI-ul PFA-ului.
          Cheltuielile trec direct pe activitate, cu regulile de 50% / 100%.
        </p>

        <h3>Leasing financiar sau operațional</h3>
        <p>
          Contractul de leasing este pe PFA, plățile sunt cheltuieli deductibile. Foaia
          de parcurs e necesară pentru deducerea integrală (100%).
        </p>

        <h2>Obligația de a întocmi foi de parcurs</h2>
        <p>
          Conform <strong>pct. 16 alin. (2)</strong> din Normele metodologice (aplicare
          art. 25 din Codul Fiscal), pentru deducerea integrală a cheltuielilor la
          calculul venitului net anual, PFA-ul trebuie să facă dovada utilizării exclusiv
          economice <strong>pe baza foii de parcurs</strong> care conține:
        </p>
        <ul>
          <li>categoria vehiculului utilizat;</li>
          <li>scopul și locul deplasării;</li>
          <li>kilometrii parcurși;</li>
          <li>norma proprie de consum carburant pe kilometru parcurs.</li>
        </ul>
        <p>
          Pentru deducerea TVA-ului (dacă PFA-ul este plătitor), se aplică suplimentar
          <strong> pct. 68 alin. (2)</strong> din normele art. 298. În practică, cele două
          seturi de cerințe sunt identice.
        </p>

        <h2>Trasee tipice pentru un PFA</h2>
        <p>
          Pentru a justifica utilizarea exclusiv economică, scopul deplasării trebuie să
          fie <strong>concret și demonstrabil</strong>. Exemple corecte pentru un PFA
          consultant / programator / contabil / arhitect:
        </p>
        <ul>
          <li>„Vizită client SC Alpha SRL, str. X, București — discuție proiect"</li>
          <li>„Deplasare la ANAF Timișoara — depunere declarație 200"</li>
          <li>„Bancă (sediu central) — semnare contract cont nou"</li>
          <li>„Oficiu poștal — expediere facturi clienți"</li>
          <li>„Achiziție consumabile birou — magazin Y"</li>
        </ul>
        <div className="article-doc__callout article-doc__callout--warning">
          <strong>De evitat</strong>
          <p>
            „Deplasare profesională", „interes serviciu", „diverse" — sunt formulări vagi
            pe care ANAF le respinge la control. Fiecare cursă are nevoie de
            <strong> destinație concretă</strong> și <strong>motiv legat de activitate</strong>.
          </p>
        </div>

        <h2>Atenție la traseul casă–birou</h2>
        <p>
          Deplasarea de la domiciliu la „sediul" PFA-ului (dacă e diferit) trebuie tratată
          cu grijă. Conform interpretării ANAF, drumurile tur–retur casă–serviciu pot fi
          considerate <strong>scop personal</strong> dacă nu sunt parte dintr-un traseu
          de lucru. La PFA cu sediul declarat la domiciliu, problema nu apare; pentru PFA
          cu sediu separat, recomandarea este să incluzi aceste segmente în foaia de
          parcurs cu scop clar (ex. „pornire activitate zilnică"). Am scris separat{" "}
          <Link to="/foaie-parcurs-casa-serviciu">când traseul casă–serviciu e personal și când poate fi de lucru</Link>.
        </p>

        <h2>Norma de consum — cum o stabilești</h2>
        <p>
          „Norma proprie de consum" nu este norma din cartea tehnică, ci o normă
          <strong> stabilită de PFA</strong>, realistă pentru condițiile de utilizare.
          Pași practici:
        </p>
        <ol>
          <li>Pornește de la consumul declarat de producător (mixt sau urban-extraurban).</li>
          <li>
            Ajustează cu +10–15% pentru condiții reale (oraș, trafic, AC, vehicul rulat).
          </li>
          <li>
            Documentează norma printr-o <strong>decizie internă</strong> a PFA-ului
            (formular simplu, semnat și datat).
          </li>
          <li>
            Reverifică anual și ajustează dacă vehiculul îmbătrânește sau se schimbă
            condițiile.
          </li>
        </ol>
        <p>
          Pașii compleți — calcul, decizie internă și ce verifică ANAF — sunt în{" "}
          <Link to="/norma-de-consum-foaie-parcurs">ghidul despre norma de consum</Link>.
        </p>

        <h2>Model simplificat pentru PFA cu un singur vehicul</h2>
        <p>
          O foaie de parcurs lunară pentru PFA poate avea o structură mai simplă decât la
          o flotă. Iată secțiunile recomandate:
        </p>

        <h3>Antetul foii</h3>
        <p>
          Denumire PFA, CUI, vehicul (marcă, nr. înmatriculare, categorie), șofer
          (titular PFA), perioada (luna și anul), norma proprie de consum, km la început
          de lună.
        </p>

        <h3>Tabelul rutelor</h3>
        <p>
          Un rând per cursă, cu data, plecare, sosire, scop concret și kilometri. Scopul
          trebuie să fie legat de activitatea economică.
        </p>

        <h3>Tabelul alimentărilor</h3>
        <p>
          Opțional, dar puternic recomandat: data, litri, preț/L, total, benzinărie.
          Permite reconcilierea cu bonurile de combustibil.
        </p>

        <h3>Sumarul lunii</h3>
        <p>
          Total km, km final odometru, consum estimat și combustibil rămas în rezervor.
          Validează coerența între km și litri.
        </p>

        <h3>Semnătura</h3>
        <p>
          Semnătura titularului PFA pe foaie. La PFA, o singură semnătură e suficientă.
        </p>

        <h2>Greșeli specifice PFA-urilor</h2>

        <h3>Comodat lipsă sau neînregistrat</h3>
        <p>
          Dacă mașina e a persoanei fizice, fără comodat scris și semnat nu poți deduce
          nimic, indiferent de foaie. Comodatul e primul pas.
        </p>

        <h3>Lipsa contractului la control</h3>
        <p>
          Păstrează o copie cu data certă (act notarial sau ANAF). Un comodat „semnat
          ieri" pentru cheltuieli de doi ani nu ajută.
        </p>

        <h3>Foi completate pe estimare</h3>
        <p>
          Foile făcute la sfârșit de an, cu același tipar, sunt vizibile la control.
          Inspectorul observă diferențele de scris și ritmul nenatural.
        </p>

        <h3>Combustibil în vacanță sau weekend</h3>
        <p>
          Alimentări în zile când nu ai trasee economice ridică suspiciuni. Dacă mașina
          e folosită mixt, regimul de 50% e mai sigur.
        </p>

        <h2>50% sau 100% — ce alegi pentru PFA</h2>
        <div className="article-doc__table-wrap">
          <table className="article-doc__table">
            <thead>
              <tr>
                <th>Variantă</th>
                <th>Foaie obligatorie</th>
                <th>Avantaj</th>
                <th>Dezavantaj</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>50% deducere</td>
                <td>nu</td>
                <td>fără birocrație, fără risc la control</td>
                <td>pierzi jumătate din deducerea pe cheltuieli auto</td>
              </tr>
              <tr>
                <td>100% deducere</td>
                <td>da, completate corect</td>
                <td>economie reală pe impozit și TVA</td>
                <td>disciplină lunară de completare</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          Pentru un PFA cu cheltuieli auto de 800 lei/lună, diferența 50% vs 100% e ~150
          lei/lună, ~1.800 lei/an. Decizia depinde de cât de des folosești mașina în scop
          economic și cât de disciplinat ești la completare. Cu o aplicație care
          completează foile automat, calculul se schimbă — efortul scade aproape la zero
          și beneficiul rămâne.
        </p>
        <RelatedGuides currentPath="/foi-parcurs-pfa" />
      </ArticleLayout>
    </>
  );
}
